import { RootEpic } from 'MyTypes';
import { empty, from, of } from 'rxjs';
import { catchError, filter, map, switchMap } from 'rxjs/operators';
import { isActionOf } from 'typesafe-actions';

import { updateLocaleWith } from '@portal/common/locale';
import { ORGANIZATION_ID_KEY, RESOURCE_ID_KEY, ROUND_ID_KEY } from '@portal/common/models/data-key';
import { getLocalePrefixForGranularityKey } from '@portal/common/utility/filters-helpers';

import {
    getAgeGroupsAsync,
    getAllSmartOptionsAsync,
    getCategoriesAsync,
    getCountryLocationsSdi,
    getDataCollectionsAsync,
    getDataProjectsAsync,
    getDatasetsAsync,
    getOrganizationsAsync,
    getRoundsAsync,
} from './actions';

export const loadOrganizations: RootEpic = (action$, state$, dependencies) => {
    const { api } = dependencies;
    return action$.pipe(
        filter(isActionOf([getOrganizationsAsync.request])),
        switchMap((action) =>
            from(api.organization.getOrganizations({ page: 1, per_page: 10000 })).pipe(
                map((res) => getOrganizationsAsync.success(res.results)),
                catchError((message: string) => of(getOrganizationsAsync.failure(message)))
            )
        )
    );
};

export const loadDataCollections: RootEpic = (action$, state$, dependencies) => {
    const { api } = dependencies;
    return action$.pipe(
        filter(isActionOf([getDataCollectionsAsync.request])),
        switchMap((action) =>
            from(api.dataCollectionResources.getAll({})).pipe(
                map((res) => getDataCollectionsAsync.success(res.results)),
                catchError((message: string) => of(getDataCollectionsAsync.failure(message)))
            )
        )
    );
};

export const loadCategories: RootEpic = (action$, state$, dependencies) => {
    const { api } = dependencies;
    return action$.pipe(
        filter(isActionOf([getCategoriesAsync.request])),
        switchMap((action) =>
            from(api.content.getCategories({ page: 1, per_page: 1000 })).pipe(
                map((res) => getCategoriesAsync.success(res.results)),
                catchError((message: string) => of(getCategoriesAsync.failure(message)))
            )
        )
    );
};

export const loadDatasets: RootEpic = (action$, state$, dependencies) => {
    const { api } = dependencies;
    return action$.pipe(
        filter(isActionOf([getDatasetsAsync.request])),
        switchMap((action) =>
            from(api.datasets.get()).pipe(
                map((response) => getDatasetsAsync.success(response.results)),
                catchError((message: string) => of(getDatasetsAsync.failure(message)))
            )
        )
    );
};

export const loadProjects: RootEpic = (action$, state$, dependencies) => {
    const { api } = dependencies;
    return action$.pipe(
        filter(isActionOf([getDataProjectsAsync.request])),
        switchMap((action) =>
            from(api.dataProjects.get()).pipe(
                map((res) => getDataProjectsAsync.success(res)),
                catchError((message: string) => of(getDataProjectsAsync.failure(message)))
            )
        )
    );
};

export const loadRounds: RootEpic = (action$, state$, dependencies) => {
    const { api } = dependencies;
    return action$.pipe(
        filter(isActionOf([getRoundsAsync.request])),
        switchMap((action) =>
            from(api.rounds.get()).pipe(
                map((res) => getRoundsAsync.success(res)),
                catchError((message: string) => of(getRoundsAsync.failure(message)))
            )
        )
    );
};

export const loadAgeGroups: RootEpic = (action$, state$, dependencies) => {
    const { api } = dependencies;
    return action$.pipe(
        filter(isActionOf([getAgeGroupsAsync.request])),
        switchMap((action) =>
            from(api.getAgeGroups()).pipe(
                map((res) => getAgeGroupsAsync.success(res)),
                catchError((message: string) => of(getAgeGroupsAsync.failure(message)))
            )
        )
    );
};

export const loadCountryLocationsSdi: RootEpic = (action$, state$, dependencies) => {
    const { api } = dependencies;
    return action$.pipe(
        filter(isActionOf([getCountryLocationsSdi.request])),
        switchMap((action) =>
            from(api.data.getCountryLocationsSdi()).pipe(
                map((res) => getCountryLocationsSdi.success(res)),
                catchError((message: string) => of(getCountryLocationsSdi.failure(message)))
            )
        )
    );
};

export const loadSmartOptions: RootEpic = (action$, state$, dependencies) => {
    const { api } = dependencies;
    return action$.pipe(
        filter(isActionOf([getAllSmartOptionsAsync.request])),
        switchMap((action) =>
            from(api.settings.getSmartOptions({ page: 1, per_page: 1000 })).pipe(
                map((res) => getAllSmartOptionsAsync.success(res.results)),
                catchError((message: string) => of(getAllSmartOptionsAsync.failure(message)))
            )
        )
    );
};

const createTranslationEpic =
    (async, translationPrefix): RootEpic =>
    (action$, state$) =>
        action$.pipe(
            filter(isActionOf([async.success])),
            switchMap((action) => {
                const translations = {};
                action.payload.forEach(({ id, name }) => {
                    translations[`${translationPrefix}${id}`] = name;
                });
                updateLocaleWith(translations);

                return empty();
            })
        );

export const addRoundTranslations: RootEpic = createTranslationEpic(
    getRoundsAsync,
    getLocalePrefixForGranularityKey(ROUND_ID_KEY)
);
export const addOrganizationTranslations: RootEpic = createTranslationEpic(
    getOrganizationsAsync,
    getLocalePrefixForGranularityKey(ORGANIZATION_ID_KEY)
);
export const addDataCollectionTranslations: RootEpic = createTranslationEpic(
    getDataCollectionsAsync,
    getLocalePrefixForGranularityKey(RESOURCE_ID_KEY)
);
